import * as React from 'react';
import { ApolloQueryResult } from '@apollo/client';
import { Switch, Menu, Transition } from '@headlessui/react';
import { AiOutlineCloseCircle } from 'react-icons/ai';
import { FaChevronDown } from 'react-icons/fa6';
import { FaLock } from 'react-icons/fa';
import useViewer from 'hooks/useViewer';
import Modal, { ModalWidths } from 'components/Modal';
import Link from 'components/Link';
import { PRICING_PAGE } from 'constants/pages';
import { TabNav } from './TabNav';
import {
  Exact,
  useUpdateUserOptionFlowFiltersMutation,
  useGetUserFlowConfigurationsQuery,
  GetUserFlowConfigurationsQuery,
} from 'types/generated/client';
import { normalizeFlowFilterState, State } from 'utils/shared/trades/presets';
import {
  useCommandCenter,
  DEFAULT_COMMAND_CENTER_STATE,
  getFlowFilterFunction,
  getDarkPoolFilterFunction,
} from 'hooks/useCommandCenter';
import { useFlowSidePreviewCount } from 'hooks/useFlowSidePreviewCount';
import Skeleton from 'components/Loading/Skeleton';
import { TickerAggregatedDetails } from 'components/OptionAggregatedTable/props';
import { OptionFlowFilters } from './OptionFlowFilters';
import { DarkPollFilters } from './DarkPoolFilters';
import { Trade, EquityPrint } from 'utils/shared/trades/types';
import { ListFilterModal } from 'layouts/FlowPageContainer/ListFilterModal';
import { SaveFilterModal } from 'layouts/FlowPageContainer/SaveFilterModal';
import classNames from 'styles/utils/classNames';
import {
  CommandContent,
  CommandBody,
  CommandFooter,
  CommandApplyButton,
  CommandCancelButton,
  ResetBox,
  ClearFiltersButton,
  FilterCountBox,
} from './styles';
import FiltersButton from './FiltersButton';

interface TabItem {
  label: string;
}

interface FilterItem {
  id?: string;
  index?: number;
  title: string;
}
type RefetchFiltersFn = (
  variables?: Partial<Exact<{ userId: string }>>,
) => Promise<ApolloQueryResult<GetUserFlowConfigurationsQuery>>;

interface Props {
  isCommandCenterOpen: boolean;
  setIsCommandCenterOpen: React.Dispatch<React.SetStateAction<boolean>>;
  currentFilters: State;
  setCurrentFilters: (state: State) => void;
  currentFilterCount: number;
  resetCurrentFilters: () => void;
  allOptionFlowTrades: Trade[];
  allDarkPoolTrades: EquityPrint[];
  watchListTickers: string[];
  tradesByTickerMap: Record<string, TickerAggregatedDetails>;
  tabsitems: string[];
  // Parallel to `tabsitems`: the `PresetType` for each built-in preset title. The tab order
  // no longer equals the enum, so this maps a title back to the preset it applies.
  tabPresetTypes?: number[];
  selectedTab: number;
  handleLoadFilters?: React.Dispatch<React.SetStateAction<boolean>>;
  handleDeleteFilter?: (id: string) => void;
  filtersDataForUsers: GetUserFlowConfigurationsQuery | undefined;
  getflowFiltersForUsers: (options?: any) => void;
  refetchFilters?: RefetchFiltersFn;
  handleChangeFilter: (title: string, index: number | string) => void;
  activeFilter: string;
  setActiveFilter: React.Dispatch<React.SetStateAction<string>>;
  // "Live count" preview for the trade-side filter. `previewTrades` holds the
  // previewed side's rows (fetched separately from the committed table feed);
  // `onFlowSidePreviewRequest` asks the page to fetch them. See useFlowSidePreviewCount.
  previewTrades?: Trade[];
  isPreviewLoading?: boolean;
  onFlowSidePreviewRequest?: (tradeSides: string[]) => void;
  // Free / anonymous viewers: Mid / Bid / Below Bid sides are locked behind an upgrade CTA.
  arePremiumSidesLocked?: boolean;
  // Baseline ("full") feed toggle. Rendered whenever `showBaselineToggle` (feature flag +
  // realtime flow active) — shown to free + paid. Interactive only when `canUseBaseline`
  // (paid); free users see it locked as an upsell. `showBaseline` on ⇒ the page re-subscribes
  // to the full firehose server-side.
  showBaselineToggle?: boolean;
  canUseBaseline?: boolean;
  showBaseline?: boolean;
  onToggleBaseline?: (next: boolean) => void;
}

const tabs: TabItem[] = [{ label: 'Options Flow Filters' }, { label: 'Dark Pool Filters' }];

const CommandCenter: React.FC<React.PropsWithChildren<Props>> = ({
  isCommandCenterOpen,
  setIsCommandCenterOpen,
  currentFilters,
  currentFilterCount,
  setCurrentFilters,
  resetCurrentFilters,
  allOptionFlowTrades,
  allDarkPoolTrades,
  watchListTickers,
  tradesByTickerMap,
  tabsitems,
  tabPresetTypes,
  selectedTab,
  handleDeleteFilter,
  filtersDataForUsers,
  handleChangeFilter,
  refetchFilters,
  activeFilter,
  setActiveFilter,
  previewTrades,
  isPreviewLoading,
  onFlowSidePreviewRequest,
  arePremiumSidesLocked,
  showBaselineToggle,
  canUseBaseline,
  showBaseline,
  onToggleBaseline,
}) => {
  const { state, dispatch, ActionTypes } = useCommandCenter();

  const { isPreviewActive, isCountLoading } = useFlowSidePreviewCount({
    isOpen: isCommandCenterOpen,
    draftFlowSideBasis: state.flowSideBasis,
    committedFlowSideBasis: currentFilters.flowSideBasis,
    requestPreview: onFlowSidePreviewRequest,
    isFetchingPreview: isPreviewLoading,
  });

  // While previewing a different side, count its freshly-fetched rows instead of
  // the committed feed (which only holds the applied side).
  const optionFlowCountTrades =
    isPreviewActive && previewTrades ? previewTrades : allOptionFlowTrades;
  const [updateFiltersMutation] = useUpdateUserOptionFlowFiltersMutation();
  const [activeTab, setActiveTab] = React.useState(0);
  const [isSaveModalOpen, setIsSaveModalOpen] = React.useState(false);
  const [isListModalOpen, setIsListModalOpen] = React.useState(false);

  const isPreciseFiltersEnabled = state.isPreciseFiltersEnabled;
  const viewer = useViewer();

  const { data: flowfilterData, refetch: refetchFlow } = useGetUserFlowConfigurationsQuery({
    variables: {
      userId: viewer?.viewer?.uid ?? '',
    },
    fetchPolicy: 'network-only',
  });

  React.useEffect(() => {
    if (isCommandCenterOpen) {
      dispatch({ type: ActionTypes.Set, payload: { items: currentFilters } });
    }
  }, [isCommandCenterOpen, currentFilters]);

  const filteredTrades = React.useMemo(() => {
    if (!isCommandCenterOpen) {
      return { filteredTradeCount: 0, watchListTradeCount: 0 };
    }
    const flowFilterFunction = getFlowFilterFunction(state, tradesByTickerMap);
    const watchlist =
      optionFlowCountTrades.length > 1 && watchListTickers.length > 1
        ? new Set(watchListTickers)
        : null;
    let filteredCount = 0;
    let watchListCount = 0;
    for (let i = 0; i < optionFlowCountTrades.length; i++) {
      const trade = optionFlowCountTrades[i];
      if (flowFilterFunction(trade)) {
        filteredCount++;
        if (
          trade.ticker &&
          (watchlist ? watchlist.has(trade.ticker) : watchListTickers.includes(trade.ticker))
        ) {
          watchListCount++;
        }
      }
    }
    return { filteredTradeCount: filteredCount, watchListTradeCount: watchListCount };
  }, [optionFlowCountTrades, state, tradesByTickerMap, watchListTickers, isCommandCenterOpen]);

  const filteredDarkPoolTrades = React.useMemo(() => {
    if (!isCommandCenterOpen) {
      return { filteredTradeCount: 0, watchListTradeCount: 0 };
    }
    const darkPoolFilterFunction = getDarkPoolFilterFunction(state);
    const watchlist =
      allDarkPoolTrades.length > 1 && watchListTickers.length > 1
        ? new Set(watchListTickers)
        : null;
    let filteredCount = 0;
    let watchListCount = 0;
    for (let i = 0; i < allDarkPoolTrades.length; i++) {
      const trade = allDarkPoolTrades[i];
      if (darkPoolFilterFunction(trade)) {
        filteredCount++;
        if (
          trade.ticker &&
          (watchlist ? watchlist.has(trade.ticker) : watchListTickers.includes(trade.ticker))
        ) {
          watchListCount++;
        }
      }
    }
    return { filteredTradeCount: filteredCount, watchListTradeCount: watchListCount };
  }, [allDarkPoolTrades, state, watchListTickers, isCommandCenterOpen]);

  const allFilters = React.useMemo(() => {
    const savedConfigs = flowfilterData?.flow_configurations || [];

    const savedTitles: FilterItem[] = savedConfigs.map((f) => ({
      id: f.id,
      title: f.title,
    }));

    const savedTitlesSet = new Set(savedConfigs.map((f) => f.title));

    const newTitles: FilterItem[] = tabsitems
      .filter((title: string) => !savedTitlesSet.has(title))
      .map((title: string) => {
        const position = tabsitems.indexOf(title);
        // Built-in preset entries carry their `PresetType` as `index` so handleChangeFilter
        // resolves the right preset (tab order no longer equals the enum value).
        return {
          index: tabPresetTypes ? tabPresetTypes[position] : position,
          title,
        };
      });

    return [...savedTitles, ...newTitles];
  }, [flowfilterData, tabsitems, tabPresetTypes]);

  React.useEffect(() => {
    // selectedTab is -1 when the current filters match no named preset ("custom"); leave
    // the label untouched in that case rather than blanking it with tabsitems[-1].
    if (tabsitems && selectedTab >= 0 && tabsitems[selectedTab]) {
      setActiveFilter(tabsitems[selectedTab]);
    }
  }, [tabsitems, selectedTab]);

  React.useEffect(() => {
    if (isCommandCenterOpen) {
      dispatch({ type: ActionTypes.Set, payload: { items: currentFilters } });
      if (!activeFilter && selectedTab >= 0 && tabsitems[selectedTab]) {
        setActiveFilter(tabsitems[selectedTab]);
      }
    }
  }, [isCommandCenterOpen, currentFilters, activeFilter, tabsitems, selectedTab]);

  return (
    <>
      {/* On wide screens (flowbar+) the Filters button is relocated into the top
          quick-filter bar (rendered by FlowPageContainer); below that it stays here next to
          the view dropdown. The breakpoint matches the bar so exactly one button shows. */}
      <div className="flowbar:hidden">
        <FiltersButton
          filterCount={currentFilterCount}
          onClick={() => setIsCommandCenterOpen(true)}
        />
      </div>
      <Modal
        width={ModalWidths.CoverPage}
        titleText="Filters"
        onExit={() => setIsCommandCenterOpen(false)}
        mounted={isCommandCenterOpen}
        focusDialog
        verticallyCenter
        maxHeight="100%"
        useCloseButton={false}
      >
        <CommandContent>
          {currentFilterCount > 0 && (
            <div className="px-2 sm:px-4">
              <ResetBox className="mb-1 mt-3 flex flex-col gap-2 rounded p-3 text-sm sm:flex-row sm:items-center sm:justify-between sm:gap-0 sm:p-4">
                <div className="text-base font-semibold" style={{ lineHeight: 1 }}>
                  <span className="flex flex-row items-center justify-between gap-1 sm:justify-start">
                    <span className="text-sm sm:text-base">Filters have been applied</span>
                    {currentFilterCount > 0 && (
                      <FilterCountBox className="ml-0 px-2 py-1 sm:ml-2">
                        {currentFilterCount}
                      </FilterCountBox>
                    )}
                  </span>
                </div>
                <ClearFiltersButton
                  className="flex items-center justify-end sm:justify-start"
                  onClick={() => {
                    dispatch({ type: ActionTypes.Reset });
                    resetCurrentFilters();
                    setIsCommandCenterOpen(false);
                    if (viewer?.viewer?.uid) {
                      updateFiltersMutation({
                        variables: {
                          id: viewer.viewer.uid,
                          optionFlowFilters: DEFAULT_COMMAND_CENTER_STATE,
                        },
                      });
                    }
                  }}
                >
                  <span className="mr-2 text-xs font-semibold uppercase">Clear Filters</span>
                  <AiOutlineCloseCircle />
                </ClearFiltersButton>
              </ResetBox>
            </div>
          )}
          <div className="flex flex-col gap-3 border-b border-palette-border px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5 sm:py-2">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold sm:text-xl">Filters</h3>
              <div className="block sm:hidden">
                <Switch.Group as="div" className="flex items-center">
                  <Switch
                    checked={isPreciseFiltersEnabled}
                    onChange={() => {
                      dispatch({
                        type: ActionTypes.Set,
                        payload: { items: { isPreciseFiltersEnabled: !isPreciseFiltersEnabled } },
                      });
                    }}
                    className="group relative inline-flex h-3 w-6 flex-shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none"
                  >
                    <span
                      aria-hidden="true"
                      className={`${
                        isPreciseFiltersEnabled ? 'bg-yellow-400' : 'bg-gray-600'
                      } pointer-events-none absolute mx-auto h-2 w-5 rounded-full transition-colors duration-200 ease-in-out`}
                    />
                    <span
                      aria-hidden="true"
                      className={`${isPreciseFiltersEnabled ? 'translate-x-3' : 'translate-x-0'} ${
                        isPreciseFiltersEnabled ? 'bg-white' : 'bg-gray-300'
                      } pointer-events-none absolute left-0 inline-block h-3 w-3 transform rounded-full border border-gray-400 shadow ring-0 transition-transform duration-200 ease-in-out`}
                    />
                  </Switch>
                  <Switch.Label as="span" className="ml-2 leading-none" style={{ lineHeight: 0 }}>
                    <span className="text-xs font-medium leading-none text-yellow-400">
                      Precise
                    </span>
                  </Switch.Label>
                </Switch.Group>
              </div>
            </div>

            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 sm:flex-none">
                <TabNav tabs={tabs} activeTab={activeTab} onTabClick={setActiveTab} />
              </div>
              <div className="hidden sm:block">
                <Switch.Group as="div" className="flex items-center justify-end">
                  <Switch
                    checked={isPreciseFiltersEnabled}
                    onChange={() => {
                      dispatch({
                        type: ActionTypes.Set,
                        payload: { items: { isPreciseFiltersEnabled: !isPreciseFiltersEnabled } },
                      });
                    }}
                    className="group relative inline-flex h-3 w-6 flex-shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none"
                  >
                    <span
                      aria-hidden="true"
                      className={`${
                        isPreciseFiltersEnabled ? 'bg-yellow-400' : 'bg-gray-600'
                      } pointer-events-none absolute mx-auto h-2 w-5 rounded-full transition-colors duration-200 ease-in-out`}
                    />
                    <span
                      aria-hidden="true"
                      className={`${isPreciseFiltersEnabled ? 'translate-x-3' : 'translate-x-0'} ${
                        isPreciseFiltersEnabled ? 'bg-white' : 'bg-gray-300'
                      } pointer-events-none absolute left-0 inline-block h-3 w-3 transform rounded-full border border-gray-400 shadow ring-0 transition-transform duration-200 ease-in-out`}
                    />
                  </Switch>
                  <Switch.Label as="span" className="ml-2 leading-none" style={{ lineHeight: 0 }}>
                    <span className="text-xs font-medium leading-none text-yellow-400">
                      Precise Filters
                    </span>
                  </Switch.Label>
                </Switch.Group>
              </div>
            </div>
          </div>

          {showBaselineToggle &&
            (canUseBaseline ? (
              // Paid: interactive toggle. Identical to the baseline PR — `feedClass` only becomes
              // 'full' when this is on (see OptionFlowPage), and only paid reaches this branch.
              <Switch.Group
                as="div"
                className="flex items-center justify-between gap-3 border-b border-palette-border px-3 py-3 sm:px-5"
              >
                <Switch.Label as="div" className="flex flex-col">
                  <span className="text-sm font-semibold sm:text-base">Full feed (all trades)</span>
                  <span className="text-xs text-gray-400">
                    Stream every trade, not just smart / unusual flow — more data, heavier
                    bandwidth. Switching reloads the feed.
                  </span>
                </Switch.Label>
                <Switch
                  checked={!!showBaseline}
                  onChange={(next: boolean) => onToggleBaseline?.(next)}
                  className="group relative inline-flex h-3 w-6 flex-shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none"
                >
                  <span
                    aria-hidden="true"
                    className={`${
                      showBaseline ? 'bg-yellow-400' : 'bg-gray-600'
                    } pointer-events-none absolute mx-auto h-2 w-5 rounded-full transition-colors duration-200 ease-in-out`}
                  />
                  <span
                    aria-hidden="true"
                    className={`${showBaseline ? 'translate-x-3' : 'translate-x-0'} ${
                      showBaseline ? 'bg-white' : 'bg-gray-300'
                    } pointer-events-none absolute left-0 inline-block h-3 w-3 transform rounded-full border border-gray-400 shadow ring-0 transition-transform duration-200 ease-in-out`}
                  />
                </Switch>
              </Switch.Group>
            ) : (
              // Free/anon: visible but locked — a purely decorative control (no headless-ui
              // Switch/Label) plus an upsell. There is deliberately no toggle handler here, so a
              // non-paid viewer has no code path that can set feedClass to 'full'; the /pricing
              // link is a plain anchor with no toggle interaction to interfere with navigation.
              <div className="flex items-center justify-between gap-3 border-b border-palette-border px-3 py-3 sm:px-5">
                <div className="flex flex-col">
                  <span className="flex items-center gap-1.5 text-sm font-semibold sm:text-base">
                    Full feed (all trades)
                    <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase text-yellow-400">
                      <FaLock className="h-3 w-3" />
                      Pro
                    </span>
                  </span>
                  <span className="text-xs text-gray-400">
                    Stream every trade, not just smart / unusual flow — more data, heavier
                    bandwidth.{' '}
                    <Link href={PRICING_PAGE} className="text-yellow-400 underline">
                      Upgrade to unlock
                    </Link>
                    .
                  </span>
                </div>
                <span
                  aria-hidden="true"
                  className="relative inline-flex h-3 w-6 flex-shrink-0 cursor-not-allowed items-center justify-center rounded-full opacity-40"
                >
                  <span className="pointer-events-none absolute mx-auto h-2 w-5 rounded-full bg-gray-600" />
                  <span className="pointer-events-none absolute left-0 inline-block h-3 w-3 translate-x-0 transform rounded-full border border-gray-400 bg-gray-300 shadow ring-0" />
                </span>
              </div>
            ))}

          <div className="flex flex-col justify-between gap-3 border-b border-palette-border px-3 py-3 sm:flex-row sm:items-center sm:px-5 sm:py-2">
            <h3 className="whitespace-nowrap text-base font-semibold sm:text-lg">My Filters</h3>
            <div className="flex flex-col gap-2 sm:w-full sm:flex-row sm:items-center sm:px-4">
              <div className="w-full sm:flex-1">
                <Menu as="div" className="relative block w-full text-left">
                  <div>
                    <Menu.Button className="flex w-full items-center justify-between rounded border border-palette-border bg-palette-black-1 py-2 pl-3 pr-2 text-sm sm:py-1 sm:pl-2">
                      <span className={classNames('truncate', !activeFilter && 'text-gray-500')}>
                        {activeFilter || 'Select a preset or saved filter'}
                      </span>
                      <FaChevronDown
                        className="ml-2 h-2.5 w-2.5 flex-shrink-0 text-gray-400"
                        aria-hidden="true"
                      />
                    </Menu.Button>
                  </div>
                  <Transition
                    as={React.Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 z-10 mt-2 max-h-80 w-full divide-y divide-gray-100 overflow-auto rounded-md border border-palette-border bg-palette-black-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      <div className="py-1">
                        {allFilters?.map((item, index) => {
                          return (
                            <Menu.Item key={item.id ?? `preset-${item.index ?? index}`}>
                              {({ active }) => (
                                <button
                                  className={classNames(
                                    active ? 'bg-brand-primary text-white' : 'text-white',
                                    'block w-full px-4 py-2 text-left text-sm',
                                  )}
                                  onClick={() => {
                                    setActiveFilter(item.title);
                                    handleChangeFilter(item.title, item.id ?? item.index ?? index);
                                  }}
                                >
                                  {item.title}
                                </button>
                              )}
                            </Menu.Item>
                          );
                        })}
                      </div>
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
              <div className="flex gap-2 sm:ml-auto sm:px-2">
                <button
                  className="flex-1 rounded-md bg-brand-primary px-3 py-2 text-sm font-semibold shadow-md disabled:bg-gray-400 sm:flex-none sm:px-2.5 sm:py-1"
                  onClick={() => {
                    setIsCommandCenterOpen(false);
                    setIsSaveModalOpen(true);
                  }}
                >
                  Save
                </button>
                <button
                  className="flex-1 rounded-md bg-brand-primary px-3 py-2 text-sm font-semibold shadow-md disabled:bg-gray-400 sm:flex-none sm:px-2.5 sm:py-1"
                  onClick={() => {
                    setIsCommandCenterOpen(false);
                    setIsListModalOpen(true);
                  }}
                >
                  Load
                </button>
              </div>
            </div>
          </div>
          <CommandBody className="px-3 py-1 sm:px-5">
            <div className="mt-4 w-full">
              {activeTab === 0 && (
                <OptionFlowFilters
                  enabled={isPreciseFiltersEnabled}
                  state={state}
                  dispatch={dispatch}
                  ActionTypes={ActionTypes}
                  allOptionFlowTrades={allOptionFlowTrades}
                  tradesByTickerMap={tradesByTickerMap}
                  arePremiumSidesLocked={arePremiumSidesLocked}
                />
              )}
              {activeTab === 1 && (
                <DarkPollFilters
                  enabled={isPreciseFiltersEnabled}
                  state={state}
                  dispatch={dispatch}
                  ActionTypes={ActionTypes}
                />
              )}
            </div>
          </CommandBody>
          <CommandFooter className="flex-col items-start gap-3 px-3 py-3 sm:flex-row sm:items-center sm:py-2">
            <div className="flex w-full flex-col gap-2 text-sm font-semibold text-gray-300 sm:w-auto sm:flex-row sm:gap-4">
              <div className="flex items-center justify-between sm:justify-start">
                <span>{activeTab === 0 ? 'Visible Flow Trades' : 'Visible Prints'}</span>
                <span className="ml-2 text-yellow-400">
                  {activeTab === 0 && isCountLoading ? (
                    <Skeleton width="w-8" height="h-4" className="inline-block align-middle" />
                  ) : activeTab === 0 ? (
                    filteredTrades.filteredTradeCount
                  ) : (
                    filteredDarkPoolTrades.filteredTradeCount
                  )}
                </span>
              </div>
              <div className="flex items-center justify-between sm:justify-start">
                <span>
                  {activeTab === 0 ? 'Visible Watchlist Trades' : 'Visible Watchlist Prints'}
                </span>
                <span className="ml-2 text-yellow-400">
                  {activeTab === 0 && isCountLoading ? (
                    <Skeleton width="w-8" height="h-4" className="inline-block align-middle" />
                  ) : activeTab === 0 ? (
                    filteredTrades.watchListTradeCount
                  ) : (
                    filteredDarkPoolTrades.watchListTradeCount
                  )}
                </span>
              </div>
            </div>
            <div className="flex w-full gap-2 sm:ml-auto sm:w-auto sm:items-center sm:justify-end sm:space-x-2">
              <CommandCancelButton
                className="flex-1 sm:flex-none"
                onClick={() => setIsCommandCenterOpen(false)}
              >
                Cancel
              </CommandCancelButton>
              <CommandApplyButton
                className="flex-1 sm:flex-none"
                onClick={() => {
                  const normalizedState = normalizeFlowFilterState(state);
                  setIsCommandCenterOpen(false);
                  setCurrentFilters(normalizedState);
                  if (viewer?.viewer?.uid) {
                    updateFiltersMutation({
                      variables: {
                        id: viewer.viewer.uid,
                        optionFlowFilters: normalizedState,
                      },
                    });
                  }
                  setIsListModalOpen(false);
                }}
              >
                Apply Filters
              </CommandApplyButton>
            </div>
          </CommandFooter>
        </CommandContent>
      </Modal>
      <ListFilterModal
        tickerData={[]}
        userId={viewer?.viewer?.uid ?? ''}
        isOpen={isListModalOpen}
        handleClose={() => {
          setIsListModalOpen(false);
        }}
        handleChangeFilter={handleChangeFilter}
        setIsCommandCenterOpen={setIsCommandCenterOpen}
        handleDeleteFilter={handleDeleteFilter}
        filtersDataForUsers={filtersDataForUsers}
        refetchFilters={refetchFilters}
        currentFilters={state}
        activeFilter={activeFilter}
        setActiveFilter={setActiveFilter}
      />
      <SaveFilterModal
        isOpen={isSaveModalOpen}
        handleClose={() => {
          setIsCommandCenterOpen(true);
          setIsSaveModalOpen(false);
        }}
        userId={viewer?.viewer?.uid || ''}
        currentFilters={state}
        refetchFlow={refetchFlow}
      />
    </>
  );
};

export default CommandCenter;
